import { getSupabaseBrowserClient } from './client'
import type { Provider } from '@supabase/supabase-js'

const supabase = getSupabaseBrowserClient()

// ─── Login with Google OAuth ───────────────────────────────────────────────
export async function loginWithGoogle(redirectTo?: string) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectTo ?? `${window.location.origin}/auth/callback`,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  })
  if (error) throw error
  return data
}

// ─── Login with any OAuth provider ─────────────────────────────────────────
export async function loginWithOAuth(provider: Provider, redirectTo?: string) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectTo ?? `${window.location.origin}/auth/callback`,
    },
  })
  if (error) throw error
  return data
}

// ─── Sign up with email + password ─────────────────────────────────────────
export async function signUpWithEmail(
  email: string,
  password: string,
  metadata?: Record<string, any>
) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/confirm`,
      data: metadata,
    },
  })
  if (error) throw error
  return data
}

// ─── Login with email + password ───────────────────────────────────────────
export async function loginWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
  return data
}

// ─── Logout ────────────────────────────────────────────────────────────────
export async function logout() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

// ─── Forgot password ──────────────────────────────────────────────────────
export async function resetPassword(email: string) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (error) throw error
  return data
}

// ─── Update password (logged in user) ──────────────────────────────────────
export async function updatePassword(newPassword: string) {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword,
  })
  if (error) throw error
  return data
}

// ─── Get current session ──────────────────────────────────────────────────
export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

// ─── Get current user ─────────────────────────────────────────────────────
export async function getUser() {
  const { data, error } = await supabase.auth.getUser()
  if (error) return null
  return data.user
}

// ─── MFA: Enroll TOTP ─────────────────────────────────────────────────────
export async function enrollMFA(friendlyName?: string) {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: friendlyName ?? 'Authenticator App',
  })
  if (error) throw error
  return data // { id, type, totp: { qr_code, secret, uri } }
}

// ─── MFA: Challenge + Verify ──────────────────────────────────────────────
export async function verifyMFA(factorId: string, code: string) {
  const { data: challenge, error: challengeError } =
    await supabase.auth.mfa.challenge({ factorId })
  if (challengeError) throw challengeError

  const { data, error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code,
  })
  if (error) throw error
  return data
}

// ─── MFA: List enrolled factors ───────────────────────────────────────────
export async function listMFAFactors() {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  return data
}

// ─── MFA: Unenroll ────────────────────────────────────────────────────────
export async function unenrollMFA(factorId: string) {
  const { data, error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
  return data
}

// ─── Auth state listener ──────────────────────────────────────────────────
export function onAuthStateChange(
  callback: (
    event: string,
    session: Awaited<ReturnType<typeof getSession>>
  ) => void
) {
  return supabase.auth.onAuthStateChange(callback)
}
